/*  MUI  */
import { SyncDisabled } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    Paper,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { GoogleLink } from 'application/Shared/Components/Buttons';
import { DateFormat } from 'application/Shared/Components/Core';
import { TenantPicker } from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Rank } from 'application/Shared/Components/Usage';
import { Notify, Transition } from 'application/Shared/Helpers';

/*  Modals  */
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import {
    useFetchApplicationAsync,
    usePostApplication,
    usePutApplication
} from 'hooks/dataAccess/Application/application';
import { useFetchCategory } from 'hooks/dataAccess/Category/category';

/*  NPM  */
import isValidDomain from 'is-valid-domain';

/*  Objects  */
import {
    Application,
    ApplicationCategoryAndSubCategory,
    ApplicationDescription
} from 'objects/Application';

/*  React  */
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BedrockModal } from './Modals/BedrockModal';
import BulkEdit from './Modals/BulkEdit';
import { GlobalAppModal } from './Modals/GlobalAppModal';

const load = async ({
    categories,
    setCategoryMap,
    setPersonalFinance,
    setPersonalId,
    setPersonalShopping,
    setUnknownId
}) => {
    const categoryMap = {};
    let unknownId = '';
    let personalId = '';
    let personalFinance = '';
    let personalShopping = '';
    categories?.forEach((c) => {
        const { id, category, subCategory } = c;

        if (category === 'Unknown' && subCategory === 'Unknown') {
            unknownId = id;
        }

        if (category === 'Personal') {
            if (subCategory === 'Other') {
                personalId = id;
            }

            if (subCategory === 'Finance') {
                personalFinance = id;
            }

            if (subCategory === 'Shopping') {
                personalShopping = id;
            }
        }

        categoryMap[id] = c;
    });
    setCategoryMap(categoryMap);
    setPersonalFinance(personalFinance);
    setPersonalId(personalId);
    setPersonalShopping(personalShopping);
    setUnknownId(unknownId);
};

const updateRows = ({ rowMap, setCategorized, setRowMap }) => {
    const categorized = Object.values(rowMap).filter((a) => a.category);
    setCategorized(categorized);
    setRowMap(rowMap);
};

const updateRow = ({ row, rowMap, setCategorized, setRowMap }) => {
    const { id } = row;

    updateRows({ rowMap: { ...rowMap, [id]: row }, setCategorized, setRowMap });
};

const resetBulkEdit = ({
    setBulkEditModalOpen,
    setSelectedApplications = () => {},
    setSelectedRows = () => {}
}) => {
    setBulkEditModalOpen(false);
    setSelectedApplications({});
    setSelectedRows([]);
};

const updateTenantSelection = ({
    parameters,
    setBulkEditModalOpen,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows,
    setTenantSelection,
    value
}) => {
    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    setTenantSelection(value);

    const currentParams = { ...parameters };

    // '0' is all tenants, the initial state.
    if (value[0] === '0') {
        delete currentParams.tenantSearch;
        setParameters(currentParams);

        setRerender(true);
        return;
    }

    setParameters({
        ...parameters,
        tenantSearch: value
    });

    setRerender(true);
};

const toggleFilter = ({
    filterName,
    disabledFilter,
    noUsageFilter,
    setDisabledFilter,
    setNoUsageFilter,
    setUncategorizedFilter,
    setWorkUsageOnlyFilter,
    uncategorizedFilter,
    workUsageOnlyFilter
}) => {
    if (filterName === 'disabled') {
        setDisabledFilter(!disabledFilter);
    }
    if (filterName === 'noUsage') {
        setNoUsageFilter(!noUsageFilter);
    }
    if (filterName === 'uncategorized') {
        setUncategorizedFilter(!uncategorizedFilter);
    }
    if (filterName === 'workUsageOnly') {
        setWorkUsageOnlyFilter(!workUsageOnlyFilter);
    }
};

const handleClose = ({
    rowMap,
    setCategorized,
    setRowMap,
    setViewingApplication
}) => {
    updateRows({ rowMap, setCategorized, setRowMap });
    setViewingApplication(false);
};

const handleFilterUncategorized = ({
    parameters,
    setBulkEditModalOpen,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows,
    setUncategorizedFilter,
    uncategorizedFilter
}) => {
    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    const currentParams = { ...parameters };
    delete currentParams.category;

    if (uncategorizedFilter) {
        setUncategorizedFilter(false);
        setParameters(currentParams);
        setRerender(true);
        return;
    }

    setUncategorizedFilter(true);
    setParameters({
        ...currentParams,
        category: 'null'
    });
    setRerender(true);
};

const handleFilterNoUsage = ({
    noUsageFilter,
    parameters,
    setBulkEditModalOpen,
    setNoUsageFilter,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows
}) => {
    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    if (!noUsageFilter) {
        setNoUsageFilter(true);
        setParameters({ ...parameters, usage: 0 });
        setRerender(true);
        return;
    }

    const currentParams = { ...parameters };
    delete currentParams.usage;
    setNoUsageFilter(false);
    setParameters(currentParams);
    setRerender(true);
};

const handleFilter = ({
    disabledFilter,
    filterName,
    noUsageFilter,
    parameters,
    setBulkEditModalOpen,
    setDisabledFilter,
    setNoUsageFilter,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows,
    setUncategorizedFilter,
    setWorkUsageOnlyFilter,
    uncategorizedFilter,
    workUsageOnlyFilter
}) => {
    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    const currentParams = { ...parameters };
    const filters = {
        disabled: disabledFilter,
        noUsage: noUsageFilter,
        uncategorized: uncategorizedFilter,
        workUsageOnly: workUsageOnlyFilter
    };
    delete currentParams[filterName];

    // Was Checked -> Now Unchecked
    if (filters[filterName]) {
        setParameters(currentParams);
    }

    // Was Unchecked -> Now Checked
    if (!filters[filterName]) {
        setParameters({ ...currentParams, [filterName]: true });
    }

    toggleFilter({
        disabledFilter,
        filterName,
        noUsageFilter,
        setDisabledFilter,
        setNoUsageFilter,
        setUncategorizedFilter,
        setWorkUsageOnlyFilter,
        uncategorizedFilter,
        workUsageOnlyFilter
    });
    setRerender(true);
};

/* Application changes functions */
const promptChanges = ({
    row,
    setAdding,
    setApplication,
    setBulkEditModalOpen,
    setSelectedApplications,
    setSelectedRows,
    setViewingApplication
}) => {
    const application = { ...row };

    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    setAdding(false);
    setApplication(application);
    setViewingApplication(true);
};

const addApplicationPrompt = ({
    setAdding,
    setApplication,
    setBulkEditModalOpen,
    setSelectedApplications,
    setSelectedRows,
    setViewingApplication
}) => {
    resetBulkEdit({
        setBulkEditModalOpen,
        setSelectedApplications,
        setSelectedRows
    });
    setAdding(true);
    setApplication({
        identifiers: []
    });
    setViewingApplication(true);
};

const addApplication = async ({
    application,
    getApplication,
    postApplication,
    setAdding,
    setApplication,
    setSaving,
    setViewingApplication,
    t
}) => {
    let { baseName } = application;

    baseName = baseName.trim().toLowerCase();
    try {
        baseName = new URL(baseName).hostname;
    } catch (e) {
        Notify.e(t('GlobalAdmin.Applications.urlNotvalid', { baseName }));
        return;
    }
    setSaving(true);
    const responseId = await postApplication({ baseName });
    setSaving(false);
    setViewingApplication(false);

    if (responseId) {
        setSaving(true);
        const { application: applicationResponse } = await getApplication(
            responseId
        );
        setSaving(false);
        setApplication({ ...applicationResponse });
    }

    setAdding(false);
    setViewingApplication(true);
};

const disable = async ({
    application,
    putApplication,
    rowMap,
    setCategorized,
    setRowMap
}) => {
    const success = await putApplication({
        applicationId: application.id,
        params: { disabled: true }
    });

    if (success) {
        // Update Table Row
        const row = {
            ...application,
            disabled: true
        };
        updateRow({ row, rowMap, setCategorized, setRowMap });
    }
};

const saveCategory = async ({
    application,
    categoryId,
    categoryMap,
    putApplication,
    rowMap,
    setCategorized,
    setRowMap
}) => {
    const success = await putApplication({
        applicationId: application.id,
        params: { category: categoryId }
    });

    // Update Table Row
    if (categoryMap[categoryId] && success) {
        const category = categoryMap[categoryId];
        const row = {
            ...application,
            category: category.category,
            categoryId,
            subCategory: category.subCategory
        };
        updateRow({ row, rowMap, setCategorized, setRowMap });
    }
};

const handleRows = ({ rows, rowMap, setCategorized, setRowMap }) => {
    if (!rows?.length) {
        return;
    }

    const currentRowMap = { ...rowMap };

    rows.forEach((r) => {
        currentRowMap[r.id] = r;
    });

    setRowMap(currentRowMap);

    updateRows({ rowMap: currentRowMap, setCategorized, setRowMap });
};

const AddApplicationButtons = ({
    application,
    getApplication,
    postApplication,
    setAdding,
    setApplication,
    setViewingApplication
}) => {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);

    return (
        <Box display="flex" justifyContent="space-between" width="100%">
            <Button
                onClick={() => {
                    setAdding(false);
                    setViewingApplication(false);
                }}
                color="primary"
                variant="outlined"
                style={{ marginRight: '8px' }}
            >
                {t('Common.cancel')}
            </Button>
            <Box display="flex">
                {saving && (
                    <CircularProgress size="32px" sx={{ marginRight: '8px' }} />
                )}
                <Button
                    disabled={saving}
                    onClick={() =>
                        addApplication({
                            application,
                            getApplication,
                            postApplication,
                            setAdding,
                            setApplication,
                            setSaving,
                            setViewingApplication,
                            t
                        })
                    }
                    color="primary"
                    variant="contained"
                >
                    {t('Common.save')}
                </Button>
            </Box>
        </Box>
    );
};

const AddApplicationModal = ({
    adding,
    application,
    getApplication,
    postApplication,
    setAdding,
    setApplication,
    setViewingApplication
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            onClose={() => {
                setAdding(false);
                setViewingApplication(false);
            }}
            open={adding}
            aria-labelledby="add-application-modal"
            TransitionComponent={Transition}
            fullWidth
            maxWidth="sm"
            title={t('GlobalAdmin.Application.addNew')}
        >
            <ModalBody>
                <Grid item xs={12}>
                    <Typography
                        variant="span"
                        color="textSecondary"
                        fontWeight={600}
                    >
                        {t('Common.enterURL')}
                    </Typography>
                    <br />
                    <TextField
                        fullWidth
                        onChange={(e) =>
                            setApplication({
                                ...application,
                                baseName: e.target.value
                            })
                        }
                        value={application?.baseName || ''}
                    />
                </Grid>
            </ModalBody>

            <ModalFooter>
                <AddApplicationButtons
                    application={application}
                    getApplication={getApplication}
                    postApplication={postApplication}
                    setAdding={setAdding}
                    setApplication={setApplication}
                    setViewingApplication={setViewingApplication}
                />
            </ModalFooter>
        </Modal>
    );
};

const headers = (t) => [
    {
        renderHeader: <strong>{t('Common.edit')}</strong>,
        width: '5%'
    },
    {
        align: 'left',
        field: 'ranking',
        renderHeader: <strong>{t('Common.rank')}</strong>,
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'usage',
        width: '5%'
    },
    {
        align: 'left',
        field: 'name',
        filterable: true,
        filterableOptions: [
            {
                parameter: 'isDesktopApp',
                render: <span>{t('Common.desktop_app')}</span>,
                value: true
            },
            {
                parameter: 'isWebApp',
                render: <span>{t('Common.web_app')}</span>,
                value: true
            }
        ],
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'name',
        width: '20%'
    },
    {
        align: 'left',
        field: 'dateCreated',
        renderHeader: <strong>{t('Common.dateAdded')}</strong>,
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'dateCreated',
        width: '10%'
    },
    {
        align: 'left',
        field: 'category',
        renderHeader: <strong>{t('Common.category')}</strong>,
        width: '15%'
    },
    {
        align: 'left',
        field: 'expertise',
        renderHeader: <strong>{t('Common.description')}</strong>,
        width: '25%'
    },
    {
        align: 'right',
        field: null,
        renderHeader: <strong>{t('Common.actions')}</strong>,
        width: '20%'
    }
];

const ActionButtons = ({
    categoryMap,
    personalId,
    putApplication,
    row,
    rowMap,
    selectedRows,
    setApplication,
    setBedrockModalOpen,
    setBulkEditModalOpen,
    setCategorized,
    setRowMap,
    unknownId
}) => {
    const { t } = useTranslation();

    if (selectedRows.indexOf(row.id) !== -1) {
        return (
            <TableCell>
                <Button
                    onClick={() => {
                        setBulkEditModalOpen(true);
                    }}
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ float: 'right' }}
                >
                    {t('GlobalAdmin.Applications.bulkEdit', {
                        count: selectedRows.length
                    })}
                </Button>
            </TableCell>
        );
    }

    return (
        <TableCell align="right">
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Button
                        onClick={() =>
                            saveCategory({
                                application: row,
                                categoryId: personalId,
                                categoryMap,
                                putApplication,
                                rowMap,
                                setCategorized,
                                setRowMap
                            })
                        }
                        color="primary"
                        variant="outlined"
                        style={{ marginRight: '4px' }}
                    >
                        {t('Common.personalOther')}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        onClick={() =>
                            saveCategory({
                                application: row,
                                categoryId: unknownId,
                                categoryMap,
                                putApplication,
                                rowMap,
                                setCategorized,
                                setRowMap
                            })
                        }
                        color="primary"
                        variant="outlined"
                        style={{ marginRight: '4px' }}
                    >
                        {t('Common.unknown')}
                    </Button>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sx={{
                        alignContent: 'center',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                            disable({
                                application: row,
                                putApplication,
                                rowMap,
                                setCategorized,
                                setRowMap
                            })
                        }
                    >
                        <SyncDisabled />
                    </IconButton>
                    <IconButton size="small" color="error">
                        <GoogleLink searchField={row.baseName} />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                            setApplication(row);
                            setBedrockModalOpen(true);
                        }}
                    >
                        <img
                            src="/images/aws-bedrock.png"
                            alt="AI"
                            style={{ width: '32px' }}
                        />
                    </IconButton>
                </Grid>
            </Grid>
        </TableCell>
    );
};

const AppTableRow = ({
    baseName,
    categoryMap,
    dateCreated,
    id,
    personalId,
    putApplication,
    rowMap,
    selectedApplications,
    selectedRows,
    setAdding,
    setApplication,
    setBedrockModalOpen,
    setBulkEditModalOpen,
    setCategorized,
    setRowMap,
    setSelectedApplications,
    setSelectedRows,
    setViewingApplication,
    unknownId,
    webhookToken
}) => {
    if (!rowMap[id]) {
        return null;
    }

    const row = rowMap[id];
    const { disabled } = row;

    let link = '';
    try {
        link = new URL(`https://${baseName}`);
        const { hostname } = link;

        if (hostname.includes('.exe')) {
            link = '';
        }

        if (!isValidDomain(hostname)) {
            link = '';
        }
    } catch (e) {
        // invalid url
    }

    return (
        <Application data={row}>
            <TableCell>
                <Checkbox
                    checked={selectedRows.indexOf(id) > -1}
                    onChange={() => {
                        let selectedRowsUpdate = [...selectedRows];
                        const selectedApplicationsUpdate = {
                            ...selectedApplications
                        };
                        if (selectedRows.indexOf(id) > -1) {
                            selectedRowsUpdate.splice(
                                selectedRowsUpdate.indexOf(id),
                                1
                            );
                            delete selectedApplicationsUpdate[id];
                        } else {
                            selectedRowsUpdate = [...selectedRows, id];
                            selectedApplicationsUpdate[id] = row;
                        }
                        setSelectedRows(selectedRowsUpdate);
                        setSelectedApplications(selectedApplicationsUpdate);
                    }}
                />
            </TableCell>
            <TableCell align="right">
                <Rank usage={row.usage} ranking={row.ranking} />
            </TableCell>

            <TableCell>
                <ApplicationNameLinkCell
                    nameClassname="link bold underline"
                    onNameClick={() => {
                        promptChanges({
                            row,
                            setAdding,
                            setApplication,
                            setBulkEditModalOpen,
                            setSelectedApplications,
                            setSelectedRows,
                            setViewingApplication
                        });
                    }}
                />
                <small>
                    {link ? (
                        <Link
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                        >
                            {baseName}
                        </Link>
                    ) : (
                        <span>{baseName}</span>
                    )}
                    <br />
                    <Trans
                        i18nKey="GlobalAdmin.Applications.id"
                        values={{ id }}
                        components={{
                            prefix: <span style={{ userSelect: 'none' }} />
                        }}
                    />
                    <br />
                    <small>
                        <Trans
                            i18nKey="GlobalAdmin.Applications.webhookToken"
                            values={{ webhookToken }}
                            components={{
                                prefix: <span style={{ userSelect: 'none' }} />
                            }}
                        />
                    </small>
                </small>
            </TableCell>
            <TableCell>
                <DateFormat date={dateCreated} noTz />
            </TableCell>

            <TableCell align="left">
                {disabled && <SyncDisabled sx={{ color: 'button.warning' }} />}
                <ApplicationCategoryAndSubCategory size="normal" noTruncate />
            </TableCell>
            <TableCell align="left">
                <ApplicationDescription lines={5} />
            </TableCell>
            <ActionButtons
                categoryMap={categoryMap}
                personalId={personalId}
                putApplication={putApplication}
                row={row}
                rowMap={rowMap}
                setApplication={setApplication}
                selectedRows={selectedRows}
                setBedrockModalOpen={setBedrockModalOpen}
                setBulkEditModalOpen={setBulkEditModalOpen}
                setCategorized={setCategorized}
                setRowMap={setRowMap}
                unknownId={unknownId}
            />
        </Application>
    );
};

const csvFields = (t) => [
    { key: 'id', label: t('Common.unique_id') },
    { key: 'ranking', label: t('Common.rank') },
    { key: 'name', label: t('Common.name') },
    { key: 'baseName', label: t('Common.applicationBaseName') },
    { key: 'dateCreated', label: t('Common.dateAdded') },
    { key: 'category', label: t('Common.category') },
    { key: 'subCategory', label: t('Common.sub_category') },
    { key: 'description', label: t('Common.description') },
    {
        key: 'metadata.FileDescription',
        label: t('Common.file_description')
    },
    { key: 'metadata.InternalName', label: t('Common.internal_name') },
    { key: 'metadata.ProductName', label: t('Common.product_name') },
    { key: 'metadata.CompanyName', label: t('Common.company_name') },
    { key: 'identifiers', label: t('Common.applicationIdentifiers') },
    { key: 'publisherName', label: t('Common.publisher') },
    { key: 'publisherId', label: t('Common.publisherId') }
];

const searchFields = ['name', 'description', 'category', 'baseName', 'id'];

const toggles = (t) => [
    {
        defaultEnabled: true,
        parameter: { field: 'excludeDisabled', value: true },
        toggleText: t('GlobalAdmin.Applications.excludeDisabledApps')
    }
];

const AppsTable = ({
    bulkEditModalOpen,
    categorized,
    categoryMap,
    parameters,
    personalId,
    putApplication,
    rerender,
    rowMap,
    selectedApplications,
    selectedRows,
    setAdding,
    setApplication,
    setBedrockModalOpen,
    setBulkEditModalOpen,
    setCategorized,
    setRerender,
    setRowMap,
    setSelectedApplications,
    setSelectedRows,
    setViewingApplication,
    unknownId
}) => {
    const { t } = useTranslation();

    const handleRowsFn = useCallback(
        (rows) => {
            handleRows({ rowMap, rows, setCategorized, setRowMap });
        },
        [rowMap, setCategorized, setRowMap]
    );

    const tableCsvFields = useMemo(() => csvFields(t), [t]);

    const tableHeaders = useMemo(() => headers(t), [t]);

    const tableToggles = useMemo(() => toggles(t), [t]);

    return (
        <>
            <BulkEdit
                open={bulkEditModalOpen}
                categorized={categorized}
                applications={selectedApplications}
                personalId={personalId}
                closeModal={() => {
                    resetBulkEdit({
                        setBulkEditModalOpen
                    });
                    setRerender(true);
                }}
                onSave={() => {
                    resetBulkEdit({
                        setBulkEditModalOpen,
                        setSelectedApplications,
                        setSelectedRows
                    });
                    setRerender(true);
                }}
            />
            <ManagedTable
                csvExport
                csvExportFields={tableCsvFields}
                csvExportFileName="global_apps"
                handleRows={handleRowsFn}
                headerHeight={20}
                headers={tableHeaders}
                parameters={parameters}
                renderBody={(row) => (
                    <AppTableRow
                        key={row.id}
                        baseName={row.baseName}
                        categoryMap={categoryMap}
                        dateCreated={row.dateCreated}
                        id={row.id}
                        personalId={personalId}
                        putApplication={putApplication}
                        rowMap={rowMap}
                        selectedApplications={selectedApplications}
                        selectedRows={selectedRows}
                        setAdding={setAdding}
                        setApplication={setApplication}
                        setBedrockModalOpen={setBedrockModalOpen}
                        setBulkEditModalOpen={setBulkEditModalOpen}
                        setCategorized={setCategorized}
                        setRowMap={setRowMap}
                        setSelectedApplications={setSelectedApplications}
                        setSelectedRows={setSelectedRows}
                        setViewingApplication={setViewingApplication}
                        unknownId={unknownId}
                        webhookToken={row.webhookToken}
                    />
                )}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rowHeight={100}
                rowsApi="/application"
                rowsApiPreload
                rowsPerPage={5}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="usage"
                toggles={tableToggles}
            />
        </>
    );
};

const AppsTableFooter = ({
    disabledFilter,
    noUsageFilter,
    parameters,
    setAdding,
    setApplication,
    setBulkEditModalOpen,
    setDisabledFilter,
    setNoUsageFilter,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows,
    setUncategorizedFilter,
    setViewingApplication,
    setWorkUsageOnlyFilter,
    uncategorizedFilter,
    workUsageOnlyFilter
}) => {
    const { t } = useTranslation();

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            m="8px"
            marginTop="16px"
        >
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={uncategorizedFilter}
                            color="primary"
                            onChange={() =>
                                handleFilterUncategorized({
                                    parameters,
                                    setBulkEditModalOpen,
                                    setParameters,
                                    setRerender,
                                    setSelectedApplications,
                                    setSelectedRows,
                                    setUncategorizedFilter,
                                    uncategorizedFilter
                                })
                            }
                        />
                    }
                    label={<>{t('Common.uncategorized')}</>}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={noUsageFilter}
                            color="primary"
                            onChange={() =>
                                handleFilterNoUsage({
                                    noUsageFilter,
                                    parameters,
                                    setBulkEditModalOpen,
                                    setNoUsageFilter,
                                    setParameters,
                                    setRerender,
                                    setSelectedApplications,
                                    setSelectedRows
                                })
                            }
                        />
                    }
                    label={<>{t('Common.no_usage')}</>}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={workUsageOnlyFilter}
                            color="primary"
                            onChange={() =>
                                handleFilter({
                                    disabledFilter,
                                    filterName: 'workUsageOnly',
                                    noUsageFilter,
                                    parameters,
                                    setBulkEditModalOpen,
                                    setDisabledFilter,
                                    setNoUsageFilter,
                                    setParameters,
                                    setRerender,
                                    setSelectedApplications,
                                    setSelectedRows,
                                    setUncategorizedFilter,
                                    setWorkUsageOnlyFilter,
                                    uncategorizedFilter,
                                    workUsageOnlyFilter
                                })
                            }
                        />
                    }
                    label={<>{t('Common.work_usage_only')}</>}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={disabledFilter}
                            color="primary"
                            onChange={() =>
                                handleFilter({
                                    disabledFilter,
                                    filterName: 'disabled',
                                    noUsageFilter,
                                    parameters,
                                    setBulkEditModalOpen,
                                    setDisabledFilter,
                                    setNoUsageFilter,
                                    setParameters,
                                    setRerender,
                                    setSelectedApplications,
                                    setSelectedRows,
                                    setUncategorizedFilter,
                                    setWorkUsageOnlyFilter,
                                    uncategorizedFilter,
                                    workUsageOnlyFilter
                                })
                            }
                        />
                    }
                    label={<>{t('Common.disabled')}</>}
                />
            </Box>
            <Box>
                <Button
                    onClick={() =>
                        addApplicationPrompt({
                            setAdding,
                            setApplication,
                            setBulkEditModalOpen,
                            setSelectedApplications,
                            setSelectedRows,
                            setViewingApplication
                        })
                    }
                    color="primary"
                    variant="outlined"
                >
                    {t('GlobalAdmin.Application.addNew')}
                </Button>
            </Box>
        </Box>
    );
};

const TenantSearch = ({
    parameters,
    setBulkEditModalOpen,
    setParameters,
    setRerender,
    setSelectedApplications,
    setSelectedRows,
    setTenantSelection,
    tenantSelection
}) => (
    <TenantPicker
        multiple
        global
        allValue="All Tenants"
        allValueId="0"
        value={tenantSelection}
        onChange={(val) =>
            updateTenantSelection({
                parameters,
                setBulkEditModalOpen,
                setParameters,
                setRerender,
                setSelectedApplications,
                setSelectedRows,
                setTenantSelection,
                value: val
            })
        }
        style={{ marginTop: '24px' }}
    />
);

export const GlobalApplicationDashboard = () => {
    const { t } = useTranslation();
    const [adding, setAdding] = useState(false);
    const [application, setApplication] = useState({});
    const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
    const [categorized, setCategorized] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [disabledFilter, setDisabledFilter] = useState(false);
    const [noUsageFilter, setNoUsageFilter] = useState(false);
    const [parameters, setParameters] = useState({
        global: true,
        includeIdentifierEntity: true,
        includePublisher: true,
        includeUsernameRefs: true
    });
    const [personalFinance, setPersonalFinance] = useState('');
    const [personalId, setPersonalId] = useState('');
    const [personalShopping, setPersonalShopping] = useState('');
    const [rerender, setRerender] = useState(false);
    const [rowMap, setRowMap] = useState([]);
    const [selectedApplications, setSelectedApplications] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [tenantSelection, setTenantSelection] = useState('0');
    const [uncategorizedFilter, setUncategorizedFilter] = useState(false);
    const [unknownId, setUnknownId] = useState('');
    const [viewingApplication, setViewingApplication] = useState(false);
    const [workUsageOnlyFilter, setWorkUsageOnlyFilter] = useState(false);
    const [bedrockModalOpen, setBedrockModalOpen] = useState(false);

    const { categories } = useFetchCategory();

    const { postApplication } = usePostApplication(t);
    const { putApplication } = usePutApplication(t);
    const { getApplication } = useFetchApplicationAsync();

    useEffect(() => {
        load({
            categories,
            setCategoryMap,
            setPersonalFinance,
            setPersonalId,
            setPersonalShopping,
            setUnknownId
        });
    }, [categories]);

    useLayoutEffect(() => {
        if (rerender) {
            debug.dev('Rerendering globals apps table');
            setRerender(false);
        }
    }, [rerender]);

    return (
        <>
            {adding && (
                <AddApplicationModal
                    adding={adding}
                    application={application}
                    getApplication={getApplication}
                    postApplication={postApplication}
                    setAdding={setAdding}
                    setApplication={setApplication}
                    setViewingApplication={setViewingApplication}
                />
            )}
            <BedrockModal
                open={bedrockModalOpen}
                application={application}
                closeModal={() => {
                    setBedrockModalOpen(false);
                }}
            />
            {viewingApplication && !adding && (
                <GlobalAppModal
                    applicationId={application.id}
                    categories={categories}
                    categorized={categorized}
                    categoryMap={categoryMap}
                    onClose={(modalsRowMap) =>
                        handleClose({
                            rowMap: modalsRowMap,
                            setCategorized,
                            setRowMap,
                            setViewingApplication
                        })
                    }
                    personalFinance={personalFinance}
                    personalId={personalId}
                    personalShopping={personalShopping}
                    rowMap={rowMap}
                    unknownId={unknownId}
                />
            )}
            <Paper elevation={0} variant="outlined">
                <PageTitle title={t('GlobalAdmin.Applications.title')} />
                <Grid container>
                    <Grid item xs={12}>
                        <TenantSearch
                            parameters={parameters}
                            setBulkEditModalOpen={setBulkEditModalOpen}
                            setParameters={setParameters}
                            setRerender={setRerender}
                            setSelectedApplications={setSelectedApplications}
                            setSelectedRows={setSelectedRows}
                            setTenantSelection={setTenantSelection}
                            tenantSelection={tenantSelection}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AppsTable
                            bedrockModalOpen={bedrockModalOpen}
                            bulkEditModalOpen={bulkEditModalOpen}
                            categorized={categorized}
                            categoryMap={categoryMap}
                            parameters={parameters}
                            personalId={personalId}
                            putApplication={putApplication}
                            rerender={rerender}
                            rowMap={rowMap}
                            selectedApplications={selectedApplications}
                            selectedRows={selectedRows}
                            setAdding={setAdding}
                            setApplication={setApplication}
                            setBedrockModalOpen={setBedrockModalOpen}
                            setBulkEditModalOpen={setBulkEditModalOpen}
                            setCategorized={setCategorized}
                            setRerender={setRerender}
                            setRowMap={setRowMap}
                            setSelectedApplications={setSelectedApplications}
                            setSelectedRows={setSelectedRows}
                            setViewingApplication={setViewingApplication}
                            unknownId={unknownId}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AppsTableFooter
                            disabledFilter={disabledFilter}
                            noUsageFilter={noUsageFilter}
                            parameters={parameters}
                            setAdding={setAdding}
                            setApplication={setApplication}
                            setBulkEditModalOpen={setBulkEditModalOpen}
                            setDisabledFilter={setDisabledFilter}
                            setNoUsageFilter={setNoUsageFilter}
                            setParameters={setParameters}
                            setRerender={setRerender}
                            setSelectedApplications={setSelectedApplications}
                            setSelectedRows={setSelectedRows}
                            setUncategorizedFilter={setUncategorizedFilter}
                            setViewingApplication={setViewingApplication}
                            setWorkUsageOnlyFilter={setWorkUsageOnlyFilter}
                            uncategorizedFilter={uncategorizedFilter}
                            workUsageOnlyFilter={workUsageOnlyFilter}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
};
